import React, { useState, useMemo } from 'react';
import {
    Search,
    ArrowLeft,
    Users,
    Wallet,
    UserCheck,
    Download,
    Copy,
    CheckCircle,
    DollarSign,
    Building,
    Building2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api, { bondsAPI, holdingsAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';

const formatId = (value) => {
    if (!value) {
        return '';
    }
    const stringValue = String(value);
    return stringValue.length > 10
        ? `${stringValue.slice(0, 6)}...${stringValue.slice(-4)}`
        : stringValue;
};

const FABondHolders = () => {
    const navigate = useNavigate();
    const { bondId } = useParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [primarySubscriberFilter, setPrimarySubscriberFilter] = useState('all');
    const [copiedAddress, setCopiedAddress] = useState(null);

    // Fetch bond data
    const { data: bondData, isPending: bondLoading } = useQuery({
        queryKey: ['bond', bondId],
        queryFn: () => bondsAPI.getBond(bondId),
        select: createResponseSelector('bond', {})
    });

    const bond = bondData || {};

    // Fetch holdings via backend (will be filtered to PS-only holdings: no custodianId, no primarySubscriberId)
    const { data: holdingsData = [], isPending: holdersLoading } = useQuery({
        queryKey: ['bond-holdings', bondId],
        enabled: !!bondId,
        queryFn: () => holdingsAPI.getHoldingsByBond(bondId, { status: 'active' }),
        select: createResponseSelector(null, [])
    });

    const psScopedHoldings = useMemo(() => {
        if (!Array.isArray(holdingsData)) {
            return [];
        }
        return holdingsData.filter((holding) => {
            const remaining = Number(holding?.remainingTokenAmount) || 0;
            // Only show holdings that belong to PS (no custodianId and no primarySubscriberId/psId)
            const hasCustodianId = holding?.custodianId != null;
            const hasPrimarySubscriberId = holding?.primarySubscriberId != null;
            const hasPsId = holding?.psId != null;

            return remaining > 0 && !hasCustodianId && !hasPrimarySubscriberId && !hasPsId;
        });
    }, [holdingsData]);

    const primarySubscriberOptions = useMemo(() => {
        const map = new Map();
        psScopedHoldings.forEach((holding) => {
            const psId = holding?.primarySubscriberId?._id || holding?.primarySubscriberId;
            if (!psId) {
                return;
            }
            const psKey = String(psId);
            if (map.has(psKey)) {
                return;
            }
            const psName = holding?.primarySubscriberSnapshot?.name
                || holding?.primarySubscriberSnapshot?.email
                || `Primary Subscriber ${psKey.slice(-4)}`;
            map.set(psKey, {
                id: psKey,
                name: psName
            });
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [psScopedHoldings]);

    const totalHoldingsAmount = useMemo(() => {
        return psScopedHoldings.reduce((sum, holding) => sum + (Number(holding?.remainingTokenAmount) || 0), 0);
    }, [psScopedHoldings]);

    const holders = useMemo(() => {
        if (!psScopedHoldings.length) {
            return [];
        }

        return psScopedHoldings.map((holding, idx) => {
            const holderSnapshot = holding?.holderSnapshot || holding?.investorSnapshot || {};
            const investorSnapshot = holding?.investorSnapshot || {};
            const walletAddress = holderSnapshot.walletAddress || investorSnapshot.walletAddress || '';
            const displayName = holderSnapshot.name || investorSnapshot.name || holderSnapshot.email || investorSnapshot.email || 'Unknown Holder';
            const holderTypeValue = (holderSnapshot.accountType || investorSnapshot.accountType || '').toLowerCase();
            const isInstitutional = holderSnapshot.isCustodian || investorSnapshot.isCustodian || holderTypeValue === 'institutional';
            const remainingAmount = Number(holding?.remainingTokenAmount) || 0;
            const originalAmount = Number(holding?.tokenAmount) || 0;
            const percentage = totalHoldingsAmount > 0 ? ((remainingAmount / totalHoldingsAmount) * 100).toFixed(2) : '0.00';
            const primarySubscriberId = holding?.primarySubscriberId?._id || holding?.primarySubscriberId || null;

            return {
                id: holding?._id || idx,
                investorName: displayName,
                walletAddress,
                holderType: isInstitutional ? 'Institutional' : 'Retail',
                accountType: holderSnapshot.accountType || investorSnapshot.accountType || null,
                holdingAmount: remainingAmount,
                originalAmount,
                holdingPercentage: percentage,
                firstPurchaseDate: holding?.holdingsStartDate || holding?.createdAt || null,
                lastTransactionDate: holding?.updatedAt || holding?.holdingsStartDate || null,
                status: holding?.holdingsEndDate ? 'Inactive' : 'Active',
                kycStatus: holderSnapshot.kycStatus || investorSnapshot.kycStatus || 'Verified',
                primarySubscriberId: primarySubscriberId ? String(primarySubscriberId) : null,
                primarySubscriberName: holding?.primarySubscriberSnapshot?.name || holding?.primarySubscriberSnapshot?.email || null,
                email: holderSnapshot.email || investorSnapshot.email,
                holderId: holding.holderId?._id || holding.holderId,
                companyRegistrationNumber: holderSnapshot.companyRegistrationNumber || investorSnapshot.companyRegistrationNumber,
                registrationDate: holderSnapshot.createdAt || investorSnapshot.createdAt,
                walletBalance: holding.tokenBalance?.balance || 0,
            };
        }).sort((a, b) => b.holdingAmount - a.holdingAmount);
    }, [psScopedHoldings, totalHoldingsAmount]);

    // Filter holders by search and primary subscriber selection
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredHolders = holders.filter(holder => {
        const fields = [holder.investorName, holder.walletAddress, holder.primarySubscriberName, holder.holderId]
            .filter(Boolean)
            .map(value => String(value).toLowerCase());

        const matchesSearch = normalizedSearch
            ? fields.some(field => field.includes(normalizedSearch))
            : true;

        const matchesPrimarySubscriber = primarySubscriberFilter === 'all'
            || (holder.primarySubscriberId && holder.primarySubscriberId === primarySubscriberFilter);

        return matchesSearch && matchesPrimarySubscriber;
    });

    // Calculate summary stats
    const totalHolders = filteredHolders.length;
    const totalHolding = filteredHolders.reduce((sum, holder) => sum + holder.holdingAmount, 0);
    const verifiedHolders = filteredHolders.filter(h => h.kycStatus === 'Verified').length;


    const copyToClipboard = async (text, id) => {
        if (!text) {
            return;
        }
        try {
            await navigator.clipboard.writeText(text);
            setCopiedAddress(id);
            setTimeout(() => setCopiedAddress(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    if (bondLoading || holdersLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holders..." />;
    }

    if (!bond._id) {
        return (
            <div className="text-center py-12">
                <p className="text-error-600">Bond not found</p>
                <button onClick={() => navigate(-1)} className="mt-4 btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    if (holders.length === 0) {
        return (
            <div className="text-center py-12">
                <Building className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No Holdings Found</p>
                <p className="text-secondary-600 mb-4">
                    No wallet currently holds tokens for this bond.
                </p>
                <button onClick={() => navigate(-1)} className="btn-primary">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Token Holders</h1>
                            <p className="text-sm text-gray-500">
                                {bond.bondName} • {bond.isin || 'Pending ISIN'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => { /* TODO: Export functionality */ }}
                        className="btn-primary"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="card">
                    <div className="card-body">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                            <div className="lg:col-span-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search by holder name, ID, or wallet address..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-input pl-10 w-full"
                                    />
                                </div>
                            </div>
                            <div className="lg:col-span-2">
                                <select
                                    value={primarySubscriberFilter}
                                    onChange={(e) => setPrimarySubscriberFilter(e.target.value)}
                                    className="form-input w-full"
                                >
                                    <option value="all">All Primary Subscribers</option>
                                    {primarySubscriberOptions.map((ps) => (
                                        <option key={ps.id} value={ps.id}>{ps.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holders Table */}
                <div className="card">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-secondary-900">
                                Token Holders ({filteredHolders.length})
                            </h2>
                            <span className="text-sm text-secondary-600">
                                Total Held: {formatCurrency(totalHolding)}
                            </span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Holder</th>
                                    <th className="table-header-cell">Wallet</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {filteredHolders.length > 0 ? (
                                    filteredHolders.map((holder) => {
                                        // Determine if this holder is a Primary Subscriber
                                        const isPrimarySubscriber = !holder.primarySubscriberId;

                                        return (
                                            <tr key={holder.id} className="table-row">
                                                <td className="table-cell align-top">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <div className="font-medium text-secondary-900">
                                                                {holder.investorName}
                                                            </div>
                                                            {isPrimarySubscriber ? (
                                                                <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-100">
                                                                    Primary Subscriber
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    {holder.holderType === 'Institutional' && (
                                                                        <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-100">
                                                                            Institutional
                                                                        </span>
                                                                    )}
                                                                    {holder.holderType === 'Retail' && (
                                                                        <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-100">
                                                                            Retail
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-dark-gray space-y-0.5">
                                                            {holder.holderId && (
                                                                <div><span className="text-dark-gray">Holder ID:</span> <code className="text-[10px] bg-secondary-50 px-1 rounded">{holder.holderId}</code></div>
                                                            )}
                                                            {holder.primarySubscriberName && (
                                                                <div><span className="text-dark-gray">Primary Subscriber:</span> {holder.primarySubscriberName}</div>
                                                            )}
                                                            {holder.email && (
                                                                <div><span className="text-dark-gray">Email:</span> {holder.email}</div>
                                                            )}
                                                            {holder.accountType && (
                                                                <div><span className="text-dark-gray">Account:</span> {holder.accountType}</div>
                                                            )}
                                                            {holder.registrationDate && (
                                                                <div><span className="text-dark-gray">Created:</span> {formatDate(holder.registrationDate)}</div>
                                                            )}
                                                            {holder.companyRegistrationNumber && (
                                                                <div><span className="text-dark-gray">Reg #:</span> {holder.companyRegistrationNumber}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    {holder.walletAddress ? (
                                                        <div className="flex items-center space-x-2">
                                                            <code className="text-xs bg-secondary-100 px-2 py-1 rounded font-mono">
                                                                {holder.walletAddress.length > 10
                                                                    ? `${holder.walletAddress.slice(0, 6)}...${holder.walletAddress.slice(-4)}`
                                                                    : holder.walletAddress}
                                                            </code>
                                                            <button
                                                                onClick={() => copyToClipboard(holder.walletAddress, holder.id)}
                                                                className="text-secondary-600 hover:text-secondary-700"
                                                            >
                                                                {copiedAddress === holder.id ? (
                                                                    <CheckCircle className="w-4 h-4 text-success-600" />
                                                                ) : (
                                                                    <Copy className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-secondary-500">
                                                            Wallet pending linkage
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="2" className="table-cell text-center py-12">
                                            <div className="text-secondary-500">
                                                <Users className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                                                <p className="text-lg font-medium mb-2">No token holders found</p>
                                                <p className="text-sm">
                                                    {searchTerm
                                                        ? `No holders match your search "${searchTerm}"`
                                                        : 'No holders have been allocated tokens for this bond yet'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FABondHolders;
